import * as THREE from "three";

export type ARExportOptions = {
  targetMaxDimensionMeters?: {
    x?: number;
    y?: number;
    z?: number;
  };
  scaleMultiplier?: number;
  /**
   * Absolute uniform scale applied to the source geometry (source unit → m).
   * When set, overrides the target-dimension fit so the model keeps its true
   * real-world size instead of being re-scaled to a fixed dimension.
   */
  absoluteScale?: number;
};

export async function exportObjectToUSDZ(
  rootInput: THREE.Object3D,
  options: ARExportOptions = {},
): Promise<Blob | null> {
  try {
    const { USDZExporter } =
      await import("three/examples/jsm/exporters/USDZExporter.js");
    const exportRoot = rootInput.clone(true);

    makeUSDZCompatible(exportRoot);
    fitObjectForAR(exportRoot, options);

    const exporter = new USDZExporter();
    const result = await exporter.parseAsync(exportRoot, {
      quickLookCompatible: true,
    });

    disposeExportRoot(exportRoot);

    const buffer = new ArrayBuffer(result.byteLength);
    new Uint8Array(buffer).set(result);

    return new Blob([buffer], { type: "model/vnd.usdz+zip" });
  } catch (error) {
    console.error("Failed to export USDZ:", error);
    return null;
  }
}

function makeUSDZCompatible(root: THREE.Object3D): void {
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const mesh = child as THREE.Mesh<
      THREE.BufferGeometry,
      THREE.Material | THREE.Material[]
    >;

    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.geometry = mesh.geometry.clone();

    if (mesh.geometry.index) {
      mesh.geometry = mesh.geometry.toNonIndexed();
    }
    if (!mesh.geometry.attributes.normal) {
      mesh.geometry.computeVertexNormals();
    }

    const material = mesh.material;
    if (Array.isArray(material)) {
      mesh.material = material.map((entry) => toUSDZMaterial(entry));
    } else {
      mesh.material = toUSDZMaterial(material);
    }
  });
}

function toUSDZMaterial(material: THREE.Material): THREE.MeshStandardMaterial {
  if (material instanceof THREE.MeshStandardMaterial) {
    const clone = material.clone();
    sanitizeUSDZMaterial(clone);
    return clone;
  }

  const source = material as Partial<THREE.MeshStandardMaterial>;
  const clone = new THREE.MeshStandardMaterial({
    color: source.color?.clone() ?? new THREE.Color(0xffffff),
    map: source.map && hasUSDZTextureImage(source.map) ? source.map : null,
    metalness: typeof source.metalness === "number" ? source.metalness : 0,
    roughness: typeof source.roughness === "number" ? source.roughness : 1,
  });

  sanitizeUSDZMaterial(clone);
  return clone;
}

function sanitizeUSDZMaterial(material: THREE.MeshStandardMaterial): void {
  material.side = THREE.FrontSide;
  material.envMap = null;
  material.aoMap = null;
  material.lightMap = null;
  material.displacementMap = null;
  material.emissiveMap = null;
  material.alphaMap = null;

  if (material.map && !hasUSDZTextureImage(material.map)) {
    material.map = null;
  }
  if (material.normalMap && !hasUSDZTextureImage(material.normalMap)) {
    material.normalMap = null;
  }
  if (material.metalnessMap && !hasUSDZTextureImage(material.metalnessMap)) {
    material.metalnessMap = null;
  }
  if (material.roughnessMap && !hasUSDZTextureImage(material.roughnessMap)) {
    material.roughnessMap = null;
  }

  material.needsUpdate = true;
}

function hasUSDZTextureImage(texture: THREE.Texture): boolean {
  const image = texture.image as unknown;

  return (
    (typeof HTMLImageElement !== "undefined" &&
      image instanceof HTMLImageElement) ||
    (typeof HTMLCanvasElement !== "undefined" &&
      image instanceof HTMLCanvasElement) ||
    (typeof OffscreenCanvas !== "undefined" &&
      image instanceof OffscreenCanvas) ||
    (typeof ImageBitmap !== "undefined" && image instanceof ImageBitmap)
  );
}

function fitObjectForAR(root: THREE.Object3D, options: ARExportOptions): void {
  const box = new THREE.Box3().setFromObject(root);
  const center = box.getCenter(new THREE.Vector3());

  root.position.sub(new THREE.Vector3(center.x, box.min.y, center.z));

  if (options.absoluteScale && options.absoluteScale > 0) {
    // Real-world size: override the on-screen display fit with an absolute
    // source-unit → metre scale so the model keeps its true dimensions.
    root.scale.setScalar(options.absoluteScale);
  } else {
    const currentBox = new THREE.Box3().setFromObject(root);
    const size = currentBox.getSize(new THREE.Vector3());
    const scaleCandidates: number[] = [];

    if (options.targetMaxDimensionMeters?.x && size.x > 0) {
      scaleCandidates.push(options.targetMaxDimensionMeters.x / size.x);
    }
    if (options.targetMaxDimensionMeters?.y && size.y > 0) {
      scaleCandidates.push(options.targetMaxDimensionMeters.y / size.y);
    }
    if (options.targetMaxDimensionMeters?.z && size.z > 0) {
      scaleCandidates.push(options.targetMaxDimensionMeters.z / size.z);
    }

    const fitScale = scaleCandidates.length
      ? Math.min(...scaleCandidates.filter((value) => value > 0))
      : 1;
    const multiplier =
      options.scaleMultiplier && options.scaleMultiplier > 0
        ? options.scaleMultiplier
        : 1;
    const finalScale = fitScale * multiplier;

    if (Number.isFinite(finalScale) && finalScale > 0) {
      root.scale.multiplyScalar(finalScale);
    }
  }

  const finalBox = new THREE.Box3().setFromObject(root);
  root.position.y -= finalBox.min.y;
}

function disposeExportRoot(root: THREE.Object3D): void {
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const mesh = child as THREE.Mesh<
      THREE.BufferGeometry,
      THREE.Material | THREE.Material[]
    >;

    mesh.geometry.dispose();

    const material = mesh.material;
    if (Array.isArray(material)) {
      material.forEach((entry) => entry.dispose());
    } else {
      material.dispose();
    }
  });
}
